/**
 * Brief STYLE-YOURSELF-SAVED-LOOKS (Brief F)
 *
 * Drawer sub-route showing the user's saved Style Yourself looks as a
 * 2-column grid. Tap a card to open StyleYourselfResult in saved-look
 * mode (Save button pre-seeded as "Saved", Try another hidden) —
 * wiring lives in Wardrobe.tsx.
 *
 * Scope: wardrobe_combinator + style_yourself + ready + is_saved.
 * No realtime, no pagination, no long-press — v1.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { StyleYourselfHeader } from "./StyleYourselfHeader";
import { formatRelative } from "@/lib/dateFormat";

export type SavedLook = {
  id: string;
  lookbook_image_url: string;
  created_at: string;
};

type Props = {
  userId: string;
  onClose: () => void;
  onOpenDrawer: () => void;
  onTapCard: (look: SavedLook) => void;
  onEmptyCta: () => void;
};

export function SavedLooks({
  userId,
  onClose,
  onOpenDrawer,
  onTapCard,
  onEmptyCta,
}: Props) {
  const [looks, setLooks] = useState<SavedLook[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLooks(null);
    setError(null);
    (async () => {
      const { data, error: err } = await supabase
        .from("outfits")
        .select("id, lookbook_image_url, created_at")
        .eq("user_id", userId)
        .eq("input_method", "wardrobe_combinator")
        .eq("analysis_mode", "style_yourself")
        .eq("lookbook_status", "ready")
        .eq("is_saved", true)
        .not("lookbook_image_url", "is", null)
        .order("created_at", { ascending: false });
      if (!active) return;
      if (err) {
        setError(err.message);
        setLooks([]);
        return;
      }
      setLooks(
        (data ?? []).filter(
          (r): r is SavedLook =>
            typeof r.lookbook_image_url === "string" &&
            r.lookbook_image_url.length > 0,
        ),
      );
    })();
    return () => {
      active = false;
    };
  }, [userId]);

  const hamburger = (
    <button
      type="button"
      onClick={onOpenDrawer}
      aria-label="Open menu"
      style={{
        width: 36,
        height: 36,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: "#2C2820",
      }}
    >
      <i className="ti ti-menu-2" style={{ fontSize: 20 }} aria-hidden="true" />
    </button>
  );

  return (
    <div className="flex flex-col" style={{ background: "#FFFCF8" }}>
      <StyleYourselfHeader
        eyebrow="LIKE2FIT · SAVED LOOKS"
        title="Saved looks"
        onBack={onClose}
        rightAction={hamburger}
      />

      {error ? (
        <div className="l2f-empty-state">
          <p style={{ color: "#C84B2F" }}>Couldn't load saved looks.</p>
          <p style={{ fontSize: 13, color: "var(--l2f-muted)" }}>{error}</p>
        </div>
      ) : looks === null ? (
        <div className="px-3 grid grid-cols-2 gap-2.5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                aspectRatio: "3 / 4",
                borderRadius: 20,
                background: "var(--surface-2)",
              }}
            />
          ))}
        </div>
      ) : looks.length === 0 ? (
        <div className="l2f-empty-state" style={{ textAlign: "center", padding: 32 }}>
          <i
            className="ti ti-bookmark"
            style={{ fontSize: 32, color: "#C84B2F" }}
            aria-hidden="true"
          />
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 22,
              color: "#2C2820",
              margin: "10px 0 6px",
            }}
          >
            Nothing saved yet
          </p>
          <p
            className="l2f-type-secondary"
            style={{ color: "var(--l2f-muted)", margin: "0 0 16px" }}
          >
            Looks you style and save will live here.
          </p>
          <button
            type="button"
            onClick={onEmptyCta}
            style={{
              background: "var(--primary)",
              color: "var(--primary-foreground)",
              border: "none",
              borderRadius: 999,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Style something
          </button>
        </div>
      ) : (
        <div className="px-3 grid grid-cols-2 gap-2.5">
          {looks.map((look) => (
            <button
              key={look.id}
              type="button"
              onClick={() => onTapCard(look)}
              style={{
                position: "relative",
                aspectRatio: "3 / 4",
                borderRadius: 20,
                overflow: "hidden",
                border: "0.5px solid var(--border)",
                background: "var(--surface-2)",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img
                src={look.lookbook_image_url}
                alt="Saved look"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 6,
                  left: 6,
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: 11,
                  color: "#FFFFFF",
                  textShadow: "0 0 8px rgba(0,0,0,0.6)",
                }}
              >
                {formatRelative(look.created_at)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
